import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Reusable confirmation dialog: standard, destructive, or typed confirmation.
//
// The modal semantics below are part of the control. `message` is where the
// platform states a consequence the button label cannot carry, so a keyboard or
// screen-reader user who cannot reach it decides without it. So the dialog
// holds `aria-modal`, `aria-labelledby` on the visible title and
// `aria-describedby` on the message. Escape cancels. Focus enters on open and
// lands on the least destructive control. Tab wraps at both ends, and focus
// returns on close to whatever held it before. The six behaviours came from the
// DP shell's own dialog under R-24 and R-30 of the admin-app extraction.
//
// `role="alertdialog"` stays out. It is the APG role for an irreversible
// confirm, and a `getByRole('dialog')` query fails against it. Flipping the role
// would move about 60 such queries across the shell and the admin app.
// `aria-describedby` closes the functional gap on its own.
//
// Focus restore captures `document.activeElement` when the effect runs and
// calls `focus()` on it in cleanup. A mount-time effect on a trigger ref would
// steal focus on first render instead. When the captured element has left the
// DOM, the common case after a row is deleted, `focus()` on the detached node
// does nothing.
import { useState, useRef, useEffect, useCallback, useId } from 'react';
import { AlertTriangle } from 'lucide-react';
/** Everything inside the dialog a keyboard can land on, in DOM order. */
const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
/** Three-tier confirmation dialog: standard, destructive, or typed. */
export function ConfirmDialog(props) {
    const { open, onClose, onCancel, onConfirm, tier, title, message, confirmLabel, typedMatch, typedPlaceholder, } = props;
    // Both handlers are optional in the type and one of the two is required at
    // runtime. These three statements run above every hook and above the
    // `if (!open) return null` exit. So a mis-wired cancel path fails on the
    // render that mounts the page, well before the render that opens the dialog.
    // A union type with `onClose?: never` on one arm would bar both bad shapes at
    // compile time. It would also leave two throws no test can reach without
    // laundering a type, which the coverage bar refuses.
    if (onClose !== undefined && onCancel !== undefined) {
        throw new Error('ConfirmDialog: pass one of onClose or onCancel; both were supplied.');
    }
    const close = onClose ?? onCancel;
    if (close === undefined) {
        throw new Error('ConfirmDialog: one of onClose or onCancel is required.');
    }
    const [typedValue, setTypedValue] = useState('');
    const dialogRef = useRef(null);
    const cancelRef = useRef(null);
    const titleId = useId();
    const messageId = useId();
    const typedInputId = useId();
    // Reset typed value when dialog opens/closes
    useEffect(() => {
        if (open) {
            setTypedValue('');
        }
    }, [open]);
    const handleClose = useCallback(() => {
        close();
    }, [close]);
    // Escape key handler. Bound on `document` because focus may legitimately sit
    // on the overlay rather than inside the panel.
    useEffect(() => {
        if (!open)
            return;
        const handler = (e) => {
            if (e.key === 'Escape') {
                handleClose();
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open, handleClose]);
    // Initial focus, the focus trap, and focus restore on close.
    useEffect(() => {
        if (!open)
            return;
        // Non-null by construction. This effect runs only with `open` true, and the
        // early `return null` below is the one path that skips rendering the div.
        // A guard here would be a branch no test could reach.
        const dialog = dialogRef.current;
        const previouslyFocused = document.activeElement;
        // The typed tier's whole point is making the user type, so the input wins
        // there; otherwise the least destructive button does. The Cancel button
        // renders on every open path, so `cancelRef.current` is non-null here too.
        const initial = dialog.querySelector('input') ?? cancelRef.current;
        initial.focus();
        const onKeyDown = (e) => {
            if (e.key !== 'Tab')
                return;
            const focusable = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR));
            const lastIndex = focusable.length - 1;
            // Only the two ends wrap. Anywhere else the browser's own sequential
            // navigation is already correct and must not be pre-empted.
            const edge = e.shiftKey ? focusable[0] : focusable[lastIndex];
            if (document.activeElement !== edge)
                return;
            e.preventDefault();
            // An empty list makes `edge` undefined on both branches, and
            // `document.activeElement` is specified as `Element | null`, so the
            // strict `!==` above has already returned. Keep it strict: under `!=` a
            // null `activeElement` matches undefined and the next line throws. State
            // it as a property, because a property survives a refactor: on an empty
            // list, `edge` must hold a value `document.activeElement` cannot match.
            (e.shiftKey ? focusable[lastIndex] : focusable[0]).focus();
        };
        dialog.addEventListener('keydown', onKeyDown);
        return () => {
            dialog.removeEventListener('keydown', onKeyDown);
            if (previouslyFocused instanceof HTMLElement)
                previouslyFocused.focus();
        };
    }, [open]);
    if (!open)
        return null;
    const isTypedValid = tier !== 'typed' || typedValue === typedMatch;
    const isDestructive = tier === 'destructive' || tier === 'typed';
    const label = confirmLabel ?? (isDestructive ? 'Delete' : 'Confirm');
    // The confirm button reads tokens, so it follows polarity and tenant
    // branding. On the accent arm, `--color-accent` is tenant-written with no
    // contrast floor, and `.on-accent` from @zion/design-tokens is the computed
    // foreground for it.
    //
    // The critical arm paints its label from `text-surface-elevated`, the
    // panel's own colour, so the label reads as cut out of the fill. Neither
    // token is tenant-written. By sRGB relative luminance over `tokens.css`,
    // the label measures 4.83:1 in light polarity and 5.05:1 in dark. White
    // measured 3.08:1 in dark (design-7). The hover keeps the fill solid and
    // adds a ring outside the label: a `/90` fill over the panel measured
    // 4.32:1 in both polarities, under the 4.5:1 a 14 px label needs.
    //
    // The accent arm keeps its fill solid on hover for the same reason
    // (design-14). The shell picks the `.on-accent` label for the solid accent.
    // A `/90` fill bleeds the panel in and moves away from that colour: tenant
    // accent #73785f measured 4.58:1 at rest and 3.79:1 on hover in light.
    const confirmClasses = isDestructive
        ? 'bg-state-critical text-surface-elevated hover:ring-2 hover:ring-state-critical/40'
        : 'bg-accent on-accent hover:ring-2 hover:ring-accent/40';
    // The overlay paints from `--color-primary`, a @zion/design-tokens token in both
    // polarities, and matches the shell's own dialog. An ad-hoc `bg-black/50` would be
    // a literal where the system supplies a value.
    return (_jsx("div", { className: "fixed inset-0 z-50 flex items-center justify-center bg-primary/40 backdrop-blur-sm", onClick: handleClose, role: "presentation", children: _jsxs("div", { ref: dialogRef, role: "dialog", "aria-modal": "true", "aria-labelledby": titleId, "aria-describedby": messageId, tabIndex: -1, className: "relative w-full max-w-md rounded-lg bg-surface-elevated p-6 shadow-xl outline-none", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "flex items-center justify-between mb-4", children: [_jsxs("div", { className: "flex items-center gap-2", children: [isDestructive && (_jsx(AlertTriangle, { className: "h-5 w-5 text-state-critical", "aria-hidden": "true" })), _jsx("h2", { id: titleId, className: "text-lg font-semibold text-primary", children: title })] }), _jsx("button", { type: "button", onClick: handleClose, "aria-label": "Close", className: "rounded p-1 text-muted hover:text-secondary", children: "\u2715" })] }), _jsx("div", { id: messageId, className: "mb-4 text-sm text-secondary", children: message }), tier === 'typed' && typedMatch !== undefined && (_jsxs("div", { className: "mb-4", children: [_jsxs("label", { htmlFor: typedInputId, className: "block text-sm font-medium text-secondary mb-1", children: ["Type ", _jsx("span", { className: "font-mono font-bold", children: typedMatch }), " to confirm"] }), _jsx("input", { id: typedInputId, type: "text", value: typedValue, onChange: (e) => setTypedValue(e.target.value), placeholder: typedPlaceholder ?? typedMatch, className: "w-full rounded border border-border-subtle px-3 py-2 text-sm bg-surface text-primary outline-none focus:ring-2 focus:ring-accent-secondary", "data-testid": "confirm-typed-input" })] })), _jsxs("div", { className: "flex justify-end gap-2", children: [_jsx("button", { ref: cancelRef, type: "button", onClick: handleClose, className: "rounded px-4 py-2 text-sm font-medium text-secondary hover:bg-surface-muted", children: "Cancel" }), _jsx("button", { type: "button", onClick: onConfirm, disabled: !isTypedValid, "data-testid": "confirm-button", className: `rounded px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${confirmClasses}`, children: label })] })] }) }));
}
//# sourceMappingURL=ConfirmDialog.js.map